import { useCallback, useEffect, useRef } from "react";
import apiPath from "../constants/apiPath";

/**
 * Keeps an in-progress draft from being lost when a tab is closed.
 *
 * Two layers, because neither alone is enough:
 *  1. A debounced save while the user pauses typing — this is what survives a
 *     crash, a lost connection, or a killed tab that never fires an unload.
 *  2. A flush when the page is hidden/closed, to capture edits made since the
 *     last debounce tick.
 *
 * The flush uses fetch({ keepalive: true }) rather than navigator.sendBeacon:
 * sendBeacon cannot set an Authorization header, and every draft endpoint here
 * sits behind Bearer auth. keepalive lets the request outlive the page, and its
 * ~64KB body cap is far above any realistic post draft.
 *
 * The first successful save creates the draft; every later save PUTs to that id,
 * so an autosaving session leaves exactly one record rather than a trail.
 */
const useDraftAutosave = ({
  enabled = true,
  getPayload,
  createUrl,
  updateUrl, // (id) => url
  debounceMs = 3000,
  onSaved,
  // "json" (default) or "formdata". Some create endpoints (e.g. the user-panel's
  // POST /user/calender) sit behind a multipart-parsing middleware that reads
  // uploaded files off req.body.files — sending JSON there skips that
  // middleware's field parsing entirely. Update calls never need this: none of
  // the edit endpoints this hook targets require multipart.
  createEncoding = "json",
}) => {
  const idRef = useRef(null);
  const timerRef = useRef(null);
  const lastSavedRef = useRef(null);
  const inFlightRef = useRef(false);
  // The promise of whichever save() call is currently in flight, if any —
  // lets a caller that's about to decide POST-vs-PUT (handleCreateThenMark-
  // External, onFinish) actually wait for a same-instant autosave tick to
  // settle instead of racing it. cancelTick() alone only stops a FUTURE
  // (not-yet-fired) debounce timer; it can't un-send a fetch that's already
  // in flight. Without this, a debounce tick firing in the same instant the
  // user clicks "Create" reads getDraftId() as still null (the in-flight
  // save hasn't gotten its response back yet), so the click creates a
  // SECOND post — and the racing autosave then completes moments later as
  // a duplicate stray Draft. Confirmed live: exactly this produced two
  // identical posts, one Published (from the click) and one Draft (from
  // the race).
  const inFlightPromiseRef = useRef(null);
  const blockedRef = useRef(false);
  // Handlers are registered once on mount, so read live values through refs
  // instead of re-subscribing on every keystroke.
  const payloadRef = useRef(getPayload);
  const enabledRef = useRef(enabled);
  payloadRef.current = getPayload;
  enabledRef.current = enabled;

  const authHeaders = (isFormData) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` };
    // Never set Content-Type ourselves for FormData — the browser must supply
    // the multipart boundary, which it can only do if it wrote the header.
    if (!isFormData) headers["Content-Type"] = "application/json";
    return headers;
  };

  const toFormData = (payload) => {
    const fd = new FormData();
    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) fd.append(key, value);
    });
    return fd;
  };

  // Serialize to compare against the last save. Skipping identical content keeps
  // idle tabs from writing every tick.
  const serialize = (p) => {
    try {
      return JSON.stringify(p);
    } catch {
      return null;
    }
  };

  const save = useCallback(
    async ({ keepalive = false } = {}) => {
      if (!enabledRef.current) return;
      const payload = payloadRef.current?.();
      if (!payload) return;

      const snapshot = serialize(payload);
      if (!snapshot || snapshot === lastSavedRef.current) return;
      // A create must not race a second create, or we'd get two drafts. The
      // debounce timer will retry. On unload we allow it through regardless —
      // there is no later retry to fall back on.
      if (inFlightRef.current && !keepalive) return;

      const id = idRef.current;
      const url = `${apiPath.baseURL}${id ? updateUrl(id) : createUrl}`;
      const method = id ? "PUT" : "POST";
      // Only the create step may need multipart — updates always go as JSON.
      const useFormData = !id && createEncoding === "formdata";

      inFlightRef.current = true;
      const work = (async () => {
        try {
          const res = await fetch(url, {
            method,
            headers: authHeaders(useFormData),
            body: useFormData ? toFormData(payload) : JSON.stringify(payload),
            keepalive,
          });
          if (!res.ok) return;
          lastSavedRef.current = snapshot;
          if (!id) {
            const body = await res.json().catch(() => null);
            // Different create endpoints wrap the new record under different
            // keys (writing-assistant drafts under "blog", calendar posts under
            // "theme" for admin/brand or "newEntry" for the user panel).
            const newId =
              body?.data?.blog?._id ||
              body?.data?.theme?._id ||
              body?.data?.newEntry?._id ||
              body?.data?._id ||
              body?.blog?._id;
            if (newId) idRef.current = newId;
          }
          // `id` here is what the pointer held BEFORE this call — falsy only on
          // the tick that just created the record, letting callers do
          // create-only work (e.g. refresh a list) without re-doing it on every
          // later autosave tick.
          onSaved?.(idRef.current, { created: !id });
        } catch {
          // Autosave is best-effort: a failed tick must never interrupt writing.
          // The next debounce tick retries.
        } finally {
          inFlightRef.current = false;
          inFlightPromiseRef.current = null;
        }
      })();
      inFlightPromiseRef.current = work;
      await work;
    },
    [createUrl, updateUrl, onSaved, createEncoding]
  );

  // Debounced tick — call this from the editor's change handler.
  const scheduleSave = useCallback(() => {
    if (!enabledRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(), debounceMs);
  }, [save, debounceMs]);

  useEffect(() => {
    // pagehide is the reliable one: on mobile Safari and on bfcache navigations
    // beforeunload often never fires. visibilitychange covers tab switches and
    // app backgrounding, which is where most "closed it and lost my work" goes.
    const flush = () => {
      if (blockedRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      save({ keepalive: true });
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHide);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [save]);

  // Let a manual save adopt the autosaved draft (so "Save as draft" revises it
  // instead of creating a second copy), and let callers reset between drafts.
  const getDraftId = useCallback(() => idRef.current, []);
  const setDraftId = useCallback((id) => {
    idRef.current = id || null;
    lastSavedRef.current = null;
    blockedRef.current = false;
  }, []);

  // Cancel any queued debounce tick without saving. Call this at the start of a
  // real form submit so a pending autosave can't race the submit and overwrite
  // the post's status back to "draft" if the user switches tabs mid-request.
  const cancelTick = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    blockedRef.current = true;
  }, []);

  // Waits out any save() currently in flight (a debounce tick that fired a
  // moment before the caller needed a definitive answer) so getDraftId()
  // afterward reflects the truth instead of a stale null. cancelTick() alone
  // cannot do this — it only stops a future, not-yet-fired timer. Call this
  // (then cancelTick(), then getDraftId()) at the start of any real submit
  // that needs to know "does a draft already exist" before deciding
  // create-vs-update.
  const waitForPendingSave = useCallback(() => {
    return inFlightPromiseRef.current || Promise.resolve();
  }, []);

  return { scheduleSave, saveNow: save, getDraftId, setDraftId, cancelTick, waitForPendingSave };
};

export default useDraftAutosave;
